import React, { useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
	DataGrid,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { downloadEboPaymentWorkingExcel } from "../excelTemplateEboPaymentWorking";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../EboPaymentWorking";

dayjs.extend(customParseFormat);

const ReportToolbar = () => (
	<GridToolbarContainer>
		<GridToolbarColumnsButton />
		<GridToolbarFilterButton />
		<GridToolbarDensitySelector />
		<Box sx={{ flexGrow: 1 }} />
		<GridToolbarQuickFilter />
	</GridToolbarContainer>
);

export const getFieldData = (data = []) => {
	return data.map((value) => ({
			name: value?.franchise,
			key: value?.franchise,
		}));
};

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	if (data && typeof data === "object") {
		const firstArrayValue = Object.values(data).find(Array.isArray);
		if (firstArrayValue) return firstArrayValue;
		return [data];
	}
	return [];
};

const hiddenReportFields = new Set([
	"received_through_cash",
	"receivedThroughCash",
	"cash",
	"cash_amount",
	"received_through_card",
	"receivedThroughCard",
	"card",
	"card_amount",
	"gv",
]);

const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const normalizeKey = (value = "") => String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const getValue = (row, keys, fallback = "") => {
	const rowKeys = Object.keys(row || {});
	const keySet = new Set(keys.map(normalizeKey));
	const matchedKey = rowKeys.find((key) => keySet.has(normalizeKey(key)));
	return matchedKey ? row[matchedKey] : fallback;
};

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const parseMonthValue = (value) => {
	if (!value) return "";

	const stringValue = String(value).trim();
	const knownFormats = ["MMM-YY", "MMM YY", "MMM-YYYY", "MMM YYYY", "YYYY-MM", "YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"];
	const parsed = dayjs(stringValue, knownFormats, true);
	if (parsed.isValid()) return parsed.format("MMM-YY");

	const looseParsed = dayjs(stringValue);
	if (looseParsed.isValid()) return looseParsed.format("MMM-YY");

	return stringValue;
};

const getMonth = (row, fallbackDate) => {
	const value = getValue(
		row,
		[
			"month",
			"month_year",
			"monthYear",
			"MONTH",
			"date",
			"bill_date",
			"billDate",
			"invoice_date",
			"invoiceDate",
			"sale_date",
			"saleDate",
			"transaction_date",
			"transactionDate",
			"business_date",
			"businessDate",
			"posting_date",
			"postingDate",
		],
		""
	);
	if (value) return String(value);
	return dayjs(fallbackDate).format("MMM-YY");
};

const groupReportData = (rows, fallbackDate) => {
	const grouped = new Map();

	rows.forEach((row) => {
		const month = parseMonthValue(getMonth(row, fallbackDate));
		const storeName = getValue(row, ["site_name", "siteName", "store_name", "storeName", "STORE_NAME", "store", "franchise"], "");
		const sitecode = getValue(row, ["sitecode"], "");
		const gv = toNumber(getValue(row, ["gv"]));
		const receivedThroughCash = toNumber(getValue(row, ["cash", "received_through_cash", "receivedThroughCash", "cash_amount"]));
		const receivedThroughCard = toNumber(getValue(row, ["card", "received_through_card", "receivedThroughCard", "card_amount"]));
		const payment_margin_percent = getValue(row, ["payment_margin_percent"], "");
		const key = `${month}__${storeName}`;

		if (!grouped.has(key)) {
			grouped.set(key, {
				month,
				storeName,
				sitecode,
				qty: 0,
				mrpSales: 0,
				discount: 0,
				realisedSales: 0,
				realisedSalesGst: 0,
				marginValue: 0,
				receivable: 0,
				billingGst: 0,
				totalReceivable: 0,
				balanceReceivable: 0,
				balanceReceivableRaw: null,
				invoiceValue: 0,
				billingValue: 0,
				cndn: 0,
				receivedThroughCash,
				receivedThroughCard,
				gv,
				payment_margin_percent,
			});
		}

		const item = grouped.get(key);
		item.qty += toNumber(getValue(row, ["qty", "quantity", "BILL_QUANTITY"]));
		item.mrpSales += toNumber(getValue(row, ["gross_amount"]));
		item.discount += toNumber(getValue(row, ["TOTAL_DISCOUNT"]));
		item.realisedSales += toNumber(getValue(row, ["realised_value"]));
		item.realisedSalesGst += toNumber(getValue(row, ["realised_sales_gst", "realisedSalesGst", "realised_gst", "gst", "gst_on_realised"]));
		item.marginValue += toNumber(getValue(row, ["payment_margin_amt"]));
		item.receivable += toNumber(getValue(row, ["receivable"]));
		item.billingGst += toNumber(getValue(row, ["billing_gst"]));
		item.totalReceivable += toNumber(getValue(row, ["total_receivable"]));
		const balanceReceivableValue = getValue(row, ["balance_receivable", "balanceReceivable"], "");
		if (balanceReceivableValue !== "" && balanceReceivableValue !== null && balanceReceivableValue !== undefined) {
			item.balanceReceivableRaw = toNumber(balanceReceivableValue);
		}
		item.invoiceValue += toNumber(getValue(row, ["invoice_value"]));
		item.billingValue += toNumber(getValue(row, ["billing_value", "billingValue"]));
		item.cndn += toNumber(getValue(row, ["dn/cn"]));
	});

	return Array.from(grouped.values())
		.map((row) => ({
			...row,
			discountPercent: row.mrpSales ? (row.discount / row.mrpSales) * 100 : 0,
			marginOnRealised: row.payment_margin_percent,
			balanceReceivable:
				row.balanceReceivableRaw !== null && row.balanceReceivableRaw !== undefined && row.balanceReceivableRaw !== 0
					? row.balanceReceivableRaw
					:  row.totalReceivable - (row.receivedThroughCash + row.receivedThroughCard + row.gv),
		}))
		.sort((first, second) => {
			const firstMonth = dayjs(first.month, "MMM-YY");
			const secondMonth = dayjs(second.month, "MMM-YY");
			if (firstMonth.isValid() && secondMonth.isValid() && !firstMonth.isSame(secondMonth)) {
				return firstMonth.valueOf() - secondMonth.valueOf();
			}
			return String(first.storeName).localeCompare(String(second.storeName));
		});
};

function FOFOReport({ value, setValue, disable }) {
	const [localFilters, setLocalFilters] = useState({
		report_type: "",
		from_month_year: null,
		to_month_year: null,
	});
	const [loading, setLoading] = useState(false);
	const [reportData, setReportData] = useState(null);
	const [error, setError] = useState("");
    const [franchiseList, setFranchiseList] = useState([]);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const roleAccessData = roleAccess(user);

	const filters = value || localFilters;

	const tableRows = useMemo(
		() =>
			getResponseRows(reportData).map((row, index) => {
				if (!row || typeof row !== "object") {
					return {
						id: index + 1,
						value: row,
					};
				}

				return {
					...row,
					id: row?.id ?? row?._id ?? index + 1,
				};
			}),
		[reportData]
	);

	const columns = useMemo(() => {
		const fields = Array.from(
			new Set(tableRows.flatMap((row) => Object.keys(row).filter((field) => field !== "id" && !hiddenReportFields.has(field))))
		);

		return fields.map((field) => ({
			field,
			headerName: formatHeaderName(field),
			flex: 1,
			minWidth: 150,
			valueFormatter: (value) => formatCellValue(value?.value ?? value),
		}));
	}, [tableRows]);

	const groupedReportRows = useMemo(
		() => groupReportData(tableRows, filters.from_month_year),
		[tableRows, filters.from_month_year]
	);

	const downloadableRawRows = useMemo(
		() =>
			tableRows.map((row) =>
				Object.fromEntries(Object.entries(row).filter(([key]) => key !== "id" && !hiddenReportFields.has(key)))
			),
		[tableRows]
	);

	const handleChange = (key) => (event) => {
		const updatedFilters = { ...filters, [key]: event.target.value };

		if (key === "report_type") {
			setReportData(null);
			setError("");
		}

		if (typeof setValue === "function") {
			setValue(updatedFilters);
			return;
		}

		setLocalFilters(updatedFilters);
	};

	const handleGetReport = async () => {
		try {
			setLoading(true);
			setLoadingOpen(true);
            await pythonVmInstance({
                url: `/ebo-payment-working/franchise-report`,
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    "store_type": "fofo",
                    "start_date": dayjs(filters.from_month_year).startOf("month").format("YYYY-MM-DD"),
                    "end_date": dayjs(filters.to_month_year).endOf("month").format("YYYY-MM-DD"),
					"franchise": filters.report_type,
                },
                method: "GET",
            }).then((res) => {
                setReportData(res?.data);	
            });
			setLoading(false);
			setLoadingOpen(false);
		} catch (err) {
			setReportData(null);
			setLoading(false);
            setSnackBarOpen(true);
            setLoadingOpen(false);
            setSnackBar({
                ...snackBar,
                message: "Failed to fetch franchise file",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
		}
	};

	const handleDownloadReport = () => {
		if (!tableRows.length) return;

		downloadEboPaymentWorkingExcel({
			rows: groupedReportRows,
			rawRows: downloadableRawRows,
			franchise: filters.report_type,
			fromDate: filters.from_month_year,
			toDate: filters.to_month_year,
		});
	};

	const handleRangeAndAutoFetch = async (key, newValue) => {
		const updatedFilters = {
			...filters,
			[key]: newValue,
			...(key === "to_month_year" ? { report_type: "" } : {}),
		};
		if (typeof setValue === "function") setValue(updatedFilters);
		else setLocalFilters(updatedFilters);
		setReportData(null);
		setError("");
		if (key === "to_month_year") {
			setFranchiseList([]);
		}

		if (
			!updatedFilters.from_month_year ||
			!updatedFilters.to_month_year ||
			!dayjs(updatedFilters.from_month_year).isValid() ||
			!dayjs(updatedFilters.to_month_year).isValid()
		) {
			return;
		}
		if (dayjs(updatedFilters.from_month_year).isAfter(dayjs(updatedFilters.to_month_year))) {
			setError("From Month-Year must be earlier than or equal to To Month-Year");
			return;
		}

		try {
			setLoadingOpen(true);
            await pythonVmInstance({
                url: `/ebo-payment-working/franchise`,
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    "store_type": "fofo",
                    "start_date": dayjs(updatedFilters.from_month_year).startOf("month").format("YYYY-MM-DD"),
                    "end_date": dayjs(updatedFilters.to_month_year).endOf("month").format("YYYY-MM-DD"),
                },
                method: "GET",
            }).then((res) => {
                setFranchiseList(getFieldData(res?.data));
            });
			setLoadingOpen(false);
		} catch (err) {
			setReportData(null);
            setSnackBarOpen(true);
            setLoadingOpen(false);
            setSnackBar({
                ...snackBar,
                message: "Failed to fetch franchise file",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
		}
	};

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700} noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
								FOFO Report
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								Choose a month range and franchise to generate the report.
							</Typography>
						</Box>

						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1.25,
									alignItems: "center",
									"& > *": {
										flex: "1 1 180px",
										minWidth: { xs: "100%", sm: "170px" },
									},
								}}>
								<DatePicker
									views={["year", "month"]}
									openTo="month"
									label="From Month-Year"
									value={filters.from_month_year || null}
									onChange={(newValue) => {
										const resetFilters = {
											...filters,
											from_month_year: newValue,
											to_month_year: null,
											report_type: "",
										};
										if (typeof setValue === "function") setValue(resetFilters);
										else setLocalFilters(resetFilters);
										setReportData(null);
										setError("");
									}}
									disabled={disable || loading}
									slotProps={{
										textField: {
											size: "small",
											sx: {
												width: "100%",
												"& .MuiInputBase-root": {
													height: 38,
													borderRadius: 1.5,
													overflow: "hidden",
												},
												"& .MuiInputBase-input": {
													py: 0.85,
													fontSize: 13,
													textOverflow: "ellipsis",
													overflow: "hidden",
													whiteSpace: "nowrap",
												},
											},
										},
									}}
									format="MMM YYYY"
								/>

						<DatePicker
							views={["year", "month"]}
							openTo="month"
							label="To Month-Year"
							value={filters.to_month_year || null}
							onChange={(newValue) => handleRangeAndAutoFetch("to_month_year", newValue)}
							disabled={disable || loading}
							maxDate={dayjs().endOf("month")}
							minDate={filters.from_month_year ? dayjs(filters.from_month_year).startOf("month") : undefined}
							slotProps={{
								textField: {
											size: "small",
											sx: {
												width: "100%",
												"& .MuiInputBase-root": {
													height: 38,
													borderRadius: 1.5,
													overflow: "hidden",
												},
												"& .MuiInputBase-input": {
													py: 0.85,
													fontSize: 13,
													textOverflow: "ellipsis",
													overflow: "hidden",
													whiteSpace: "nowrap",
												},
											},
										},
									}}
									format="MMM YYYY"
								/>

								<TextField
									select
									size="small"
									label="Select Franchise"
									value={filters.report_type || ""}
									onChange={handleChange("report_type")}
									disabled={disable || loading}
									sx={{
										width: "100%",
										"& .MuiInputBase-root": {
											height: 38,
											borderRadius: 1.5,
											overflow: "hidden",
										},
										"& .MuiInputBase-input": {
											py: 0.85,
											fontSize: 13,
											textOverflow: "ellipsis",
											overflow: "hidden",
											whiteSpace: "nowrap",
										},
									}}>
									{franchiseList.map((option) => (
										<MenuItem key={option.key} value={option.key}>
											{option.name}
										</MenuItem>
									))}
								</TextField>

								<Button
									variant="contained"
									onClick={handleGetReport}
									disabled={disable || loading || !filters.report_type || !filters.from_month_year || !filters.to_month_year}
									size="small"
									sx={{ height: 38, px: 2, minWidth: { xs: "100%", sm: 120 }, flex: "0 0 auto" }}>
									{loading ? "Fetching..." : "Get Report"}
								</Button>

								<Button
									variant="outlined"
									startIcon={<FileDownloadIcon />}
									onClick={handleDownloadReport}
									disabled={disable || loading || tableRows.length === 0 || roleAccessData.roles === "VIEWER"}
									size="small"
									sx={{ height: 38, px: 2, minWidth: { xs: "100%", sm: 120 }, flex: "0 0 auto" }}>
									Download
								</Button>
							</Box>
						</LocalizationProvider>

						{error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
							<Typography variant="subtitle1" fontWeight={700}>
								Report Summary
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Rows: {tableRows.length.toLocaleString("en-IN")}
							</Typography>
						</Stack>

						<Box sx={{ height: { xs: "calc(100vh - 350px)", md: "calc(100vh - 280px)" }, minHeight: 340, width: "100%" }}>
							<DataGrid
								rows={tableRows}
								columns={columns}
								loading={loading}
								slots={{ toolbar: ReportToolbar }}
								density="compact"
								pageSizeOptions={[25, 50, 100]}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 25, page: 0 },
									},
								}}
								sx={{
									border: "none",
									fontSize: 13,
									"& .MuiDataGrid-row": {
										minHeight: 36,
										maxHeight: 36,
									},
									"& .MuiDataGrid-cell": {
										py: 0.5,
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f5f5f5",
										minHeight: 40,
										maxHeight: 40,
									},
								}}
							/>
						</Box>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default FOFOReport;
